import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  addDays, addMonths, addWeeks, endOfMonth, endOfWeek, format, isSameDay,
  isSameMonth, isToday, parseISO, startOfMonth, startOfWeek, subMonths, subWeeks,
} from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";
import { ConfirmDialog, EmptyState, Field, PageHeader, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const TYPES = ["Initial Consultation", "Case Review", "Document Signing", "Court Preparation", "Deposition", "Virtual Meeting"];
const inputCls = "bg-white border border-border focus:border-gold px-3 py-2.5 w-full outline-none transition text-sm rounded-none";
const EMPTY_FORM = { client_id: "", date: "", time: "10:00", duration_minutes: 60, type: "Initial Consultation", status: "Scheduled", location: "Office", notes: "" };

export default function Appointments() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [formOpen, setFormOpen] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    api.get("/clients?limit=100").then(({ data }) => setClients(data.items)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/appointments").then(({ data }) => setItems(data.items)).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const map = {};
    (items || []).forEach((a) => {
      (map[a.date] = map[a.date] || []).push(a);
    });
    return map;
  }, [items]);

  const openForm = (a, dateStr) => {
    setEditing(a);
    setForm(a ? { ...EMPTY_FORM, ...a } : { ...EMPTY_FORM, date: dateStr || format(new Date(), "yyyy-MM-dd") });
    setErrors({});
    setFormOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.client_id) errs.client_id = "Select a client";
    if (!form.date) errs.date = "Date is required";
    if (!form.time) errs.time = "Time is required";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      if (editing) await api.put(`/appointments/${editing.id}`, form);
      else await api.post("/appointments", form);
      toast.success(editing ? "Appointment updated" : "Appointment scheduled");
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const doCancel = async () => {
    try {
      await api.put(`/appointments/${cancelling.id}`, { ...cancelling, status: "Cancelled" });
      toast.success("Appointment cancelled");
      setCancelling(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const navigateCal = (dir) => {
    if (view === "month") setCursor(dir > 0 ? addMonths(cursor, 1) : subMonths(cursor, 1));
    else if (view === "week") setCursor(dir > 0 ? addWeeks(cursor, 1) : subWeeks(cursor, 1));
    else setCursor(dir > 0 ? addDays(cursor, 1) : addDays(cursor, -1));
  };

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const days = [];
    for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
    return days;
  }, [cursor]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    return [...Array(7)].map((_, i) => addDays(start, i));
  }, [cursor]);

  const listItems = useMemo(() => {
    if (!items) return [];
    const today = format(new Date(), "yyyy-MM-dd");
    return items.filter((a) => a.date >= today).slice(0, 30);
  }, [items]);

  const title = view === "month" ? format(cursor, "MMMM yyyy")
    : view === "week" ? `${format(weekDays[0], "MMM d")} – ${format(weekDays[6], "MMM d, yyyy")}`
    : format(cursor, "EEEE, MMMM d, yyyy");

  const ApptChip = ({ a, compact }) => (
    <button onClick={(e) => { e.stopPropagation(); openForm(a); }} data-testid={`appt-chip-${a.id}`}
      className={`w-full text-left px-1.5 py-1 text-[11px] leading-tight truncate transition-colors ${a.status === "Cancelled" ? "bg-gray-100 text-gray-400 line-through" : a.status === "Completed" ? "bg-emerald-50 text-emerald-800" : "bg-navy text-ivory hover:bg-navy-800"}`}>
      {fmtTime(a.time)} {compact ? "" : a.client_name}
    </button>
  );

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Appointments" description="Your calendar, your clients, your courtroom prep." testId="appointments-header">
        <Button onClick={() => openForm(null)} data-testid="add-appointment-btn"
          className="bg-navy text-ivory hover:bg-navy-800 rounded-none uppercase tracking-wider text-xs font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> Schedule
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateCal(-1)} data-testid="cal-prev" className="rounded-none"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today" className="rounded-none">Today</Button>
          <Button variant="outline" size="icon" onClick={() => navigateCal(1)} data-testid="cal-next" className="rounded-none"><ChevronRight className="h-4 w-4" /></Button>
          <h2 className="font-serif text-2xl font-semibold text-navy ml-3" data-testid="cal-title">{title}</h2>
        </div>
        <div className="flex border border-border bg-white" data-testid="view-switcher">
          {["month", "week", "day", "list"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${view === v ? "bg-navy text-ivory" : "text-muted-foreground hover:text-navy"}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {!items ? (
        <div className="bg-white border border-border h-[560px] animate-pulse" data-testid="calendar-skeleton" />
      ) : items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No appointments" description="Schedule your first consultation to see the calendar come alive."
          testId="appointments-empty"
          action={<Button onClick={() => openForm(null)} className="bg-navy text-ivory rounded-none" data-testid="empty-add-appt"><Plus className="h-4 w-4 mr-1.5" /> Schedule</Button>} />
      ) : (
        <>
          {view === "month" && (
            <div className="bg-white border border-border shadow-card" data-testid="calendar-month">
              <div className="grid grid-cols-7 border-b border-border">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div key={d} className="px-2 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground text-center">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {monthDays.map((d, i) => {
                  const key = format(d, "yyyy-MM-dd");
                  const dayAppts = byDate[key] || [];
                  return (
                    <div key={i} onClick={() => openForm(null, key)} data-testid={`cal-day-${key}`}
                      className={`min-h-[92px] sm:min-h-[112px] border-b border-r border-border p-1.5 cursor-pointer transition-colors hover:bg-gold-100/30 ${!isSameMonth(d, cursor) ? "bg-muted/40" : ""} ${(i + 1) % 7 === 0 ? "border-r-0" : ""}`}>
                      <span className={`inline-flex h-6 w-6 items-center justify-center text-xs font-semibold ${isToday(d) ? "bg-gold text-navy" : isSameMonth(d, cursor) ? "text-navy" : "text-muted-foreground/50"}`}>
                        {format(d, "d")}
                      </span>
                      <div className="mt-1 space-y-1">
                        {dayAppts.slice(0, 2).map((a) => <ApptChip key={a.id} a={a} />)}
                        {dayAppts.length > 2 && (
                          <p className="text-[10px] text-muted-foreground px-1.5">+{dayAppts.length - 2} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "week" && (
            <div className="bg-white border border-border shadow-card overflow-x-auto" data-testid="calendar-week">
              <div className="grid grid-cols-7 min-w-[840px]">
                {weekDays.map((d) => {
                  const key = format(d, "yyyy-MM-dd");
                  const dayAppts = byDate[key] || [];
                  return (
                    <div key={key} className="border-r border-border last:border-r-0">
                      <div className={`px-3 py-3 border-b border-border text-center ${isToday(d) ? "bg-gold-100" : ""}`}>
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{format(d, "EEE")}</p>
                        <p className={`font-serif text-xl font-semibold ${isToday(d) ? "text-gold-700" : "text-navy"}`}>{format(d, "d")}</p>
                      </div>
                      <div className="p-2 space-y-2 min-h-[380px]">
                        {dayAppts.map((a) => (
                          <button key={a.id} onClick={() => openForm(a)} data-testid={`week-appt-${a.id}`}
                            className={`w-full text-left p-2 border-l-2 transition-colors ${a.status === "Cancelled" ? "border-gray-300 bg-gray-50 text-gray-400" : "border-gold bg-gold-100/40 hover:bg-gold-100"}`}>
                            <p className="text-xs font-bold text-navy">{fmtTime(a.time)}</p>
                            <p className="text-xs font-semibold truncate">{a.client_name}</p>
                            <p className="text-[10px] text-muted-foreground">{a.type}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "day" && (
            <div className="bg-white border border-border shadow-card" data-testid="calendar-day">
              <div className="divide-y divide-border">
                {[...Array(12)].map((_, i) => {
                  const hour = i + 8;
                  const hourAppts = (byDate[format(cursor, "yyyy-MM-dd")] || []).filter((a) => parseInt(a.time.split(":")[0], 10) === hour);
                  return (
                    <div key={hour} className="flex min-h-[64px]">
                      <div className="w-20 shrink-0 px-3 py-3 text-xs font-semibold text-muted-foreground border-r border-border">
                        {hour % 12 || 12}:00 {hour >= 12 ? "PM" : "AM"}
                      </div>
                      <div className="flex-1 p-2 space-y-2">
                        {hourAppts.map((a) => (
                          <button key={a.id} onClick={() => openForm(a)} data-testid={`day-appt-${a.id}`}
                            className="w-full text-left p-3 border-l-2 border-gold bg-gold-100/40 hover:bg-gold-100 transition-colors flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-navy">{a.client_name} — {a.type}</p>
                              <p className="text-xs text-muted-foreground">{fmtTime(a.time)} · {a.duration_minutes} min · {a.location}</p>
                            </div>
                            <StatusBadge status={a.status} />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "list" && (
            <div className="bg-white border border-border shadow-card divide-y divide-border" data-testid="calendar-list">
              {listItems.length === 0 && <p className="px-6 py-10 text-sm text-muted-foreground">No upcoming appointments.</p>}
              {listItems.map((a) => (
                <div key={a.id} className="flex items-center gap-4 px-6 py-4 hover:bg-muted/40 transition-colors" data-testid={`list-appt-${a.id}`}>
                  <div className="w-14 text-center shrink-0 border-r border-border pr-4">
                    <p className="font-serif text-2xl font-semibold text-navy">{format(parseISO(a.date), "d")}</p>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{format(parseISO(a.date), "MMM")}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy text-sm">{a.client_name} — {a.type}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fmtTime(a.time)} · {a.duration_minutes} min · {a.location}</p>
                  </div>
                  <StatusBadge status={a.status} />
                  <div className="flex gap-1">
                    <button onClick={() => openForm(a)} className="p-2 text-muted-foreground hover:text-navy hover:bg-muted transition-colors" data-testid={`list-edit-${a.id}`} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    {a.status === "Scheduled" && (
                      <button onClick={() => setCancelling(a)} className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`list-cancel-${a.id}`} aria-label="Cancel">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditing(null); params.delete("new"); setParams(params, { replace: true }); } }}>
        <DialogContent className="max-w-xl" data-testid="appointment-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-navy">{editing ? "Edit Appointment" : "Schedule Appointment"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5 mt-2" noValidate>
            <div className="sm:col-span-2">
              <Field label="Client *" error={errors.client_id} htmlFor="appt-client">
                <select id="appt-client" className={inputCls} value={form.client_id} onChange={set("client_id")} data-testid="appt-client-select">
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Date *" error={errors.date} htmlFor="appt-date">
              <input id="appt-date" type="date" className={inputCls} value={form.date} onChange={set("date")} data-testid="appt-date-input" />
            </Field>
            <Field label="Time *" error={errors.time} htmlFor="appt-time">
              <input id="appt-time" type="time" className={inputCls} value={form.time} onChange={set("time")} data-testid="appt-time-input" />
            </Field>
            <Field label="Duration" htmlFor="appt-duration">
              <select id="appt-duration" className={inputCls} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} data-testid="appt-duration-select">
                {[30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} minutes</option>)}
              </select>
            </Field>
            <Field label="Consultation Type" htmlFor="appt-type">
              <select id="appt-type" className={inputCls} value={form.type} onChange={set("type")} data-testid="appt-type-select">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location" htmlFor="appt-location">
              <input id="appt-location" className={inputCls} value={form.location} onChange={set("location")} data-testid="appt-location-input" placeholder="Office / Virtual / Court" />
            </Field>
            <Field label="Status" htmlFor="appt-status">
              <select id="appt-status" className={inputCls} value={form.status} onChange={set("status")} data-testid="appt-status-select">
                {["Scheduled", "Completed", "Cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notes" htmlFor="appt-notes">
                <textarea id="appt-notes" rows={2} className={`${inputCls} resize-none`} value={form.notes || ""} onChange={set("notes")} data-testid="appt-notes-input" placeholder="Preparation items, documents to bring…" />
              </Field>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)} data-testid="appt-form-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="appt-form-submit" className="bg-navy text-ivory hover:bg-navy-800 rounded-none">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Schedule"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!cancelling} onOpenChange={() => setCancelling(null)}
        title="Cancel this appointment?"
        description={`${cancelling?.type} with ${cancelling?.client_name} on ${fmtDate(cancelling?.date)} at ${fmtTime(cancelling?.time)} will be marked as cancelled.`}
        confirmLabel="Cancel Appointment" onConfirm={doCancel} />
    </div>
  );
}
